import fetchCustom, { callLinkAction } from '@helpers/fetchCustom';
import getResponseJson from '@helpers/getResponseJson';
import { accessPayload, hasPendingAccess, toggleAccess } from '@components/RecruitmentFlowEditor/StepAccess/helper';

export const STEP_ACCESS = 'step_access';

const clearStep = (state, stepId) => {
    state.pending = { ...state.pending };
    delete state.pending[stepId];
    state.query = { ...state.query };
    delete state.query[stepId];
    state.directory = { ...state.directory };
    delete state.directory[stepId];
};

export const createActions = (set, get, jobId) => ({
    fetchAccess: async (signal) => {
        if (!jobId) {
            return;
        }
        set((state) => {
            state.access.loading = true;
            state.access.error = null;
        });
        try {
            const response = await fetchCustom(`/api/jobs/${jobId}/ui?expand=${STEP_ACCESS}`, {
                method: 'GET',
                signal,
            });
            if (signal?.aborted) {
                return;
            }
            const body = await getResponseJson(response, { notification: false });
            if (signal?.aborted) {
                return;
            }
            set((state) => {
                if (response.ok) {
                    state.access.data = body?.[STEP_ACCESS] ?? null;
                    state.access.error = null;
                } else {
                    state.access.data = null;
                    state.access.error = body?.notification || body?.message || 'Request failed';
                }
            });
        } catch (error) {
            if (signal?.aborted || error?.name === 'AbortError') {
                return;
            }
            set((state) => {
                state.access.data = null;
                state.access.error = error.message || 'Request failed';
            });
        } finally {
            set((state) => {
                state.access.loading = false;
                if (!signal?.aborted) {
                    state.access.loaded = true;
                }
            });
        }
    },

    toggle: (stepId, person) => {
        set((state) => {
            state.pending = { ...state.pending, [stepId]: toggleAccess(state.pending[stepId], person) };
        });
    },

    setQuery: (stepId, value) => {
        set((state) => {
            state.query = { ...state.query, [stepId]: value };
        });
    },

    search: async (stepId, signal) => {
        const step = (get().access.data?.steps ?? []).find((each) => String(each.step_id) === String(stepId));
        const term = (get().query[stepId] || '').trim();
        // The lookup URL comes from the payload, so the server decides both its scope and whether it
        // is offered at all.
        if (!step?.search_url || term === '') {
            set((state) => {
                state.directory = { ...state.directory, [stepId]: [] };
            });
            return;
        }
        set((state) => {
            state.searching = { ...state.searching, [stepId]: true };
        });
        try {
            const url = new URL(step.search_url, window.location.origin);
            url.searchParams.set('term', term);
            const response = await fetchCustom(url.href, { method: 'GET', signal });
            if (signal?.aborted) {
                return;
            }
            const body = await getResponseJson(response, { notification: false });
            if (signal?.aborted) {
                return;
            }
            set((state) => {
                state.directory = {
                    ...state.directory,
                    [stepId]: response.ok ? body?.results ?? [] : [],
                };
            });
        } catch (error) {
            if (signal?.aborted || error?.name === 'AbortError') {
                return;
            }
            set((state) => {
                state.directory = { ...state.directory, [stepId]: [] };
            });
        } finally {
            if (!signal?.aborted) {
                set((state) => {
                    state.searching = { ...state.searching, [stepId]: false };
                });
            }
        }
    },

    // The wording comes back with the response: which outcome occurred is only knowable from the
    // state before the write.
    commit: async (stepId) => {
        const links = get().access.data?._links;
        const pending = get().pending[stepId];
        if (!links?.put || !hasPendingAccess(pending)) {
            return { ok: true };
        }
        set((state) => {
            state.saving = { ...state.saving, [stepId]: true };
        });
        try {
            const response = await callLinkAction(links, 'put', {
                body: JSON.stringify({ steps: { [stepId]: accessPayload(pending) } }),
            });
            const body = await getResponseJson(response, { notification: true });
            if (!response.ok) {
                return { ok: false };
            }
            set((state) => {
                const data = body?.[STEP_ACCESS];
                if (data) {
                    state.access.data = data;
                }
                clearStep(state, stepId);
            });
            return { ok: true };
        } catch (error) {
            return { ok: false, errorMessage: error?.message || 'Request failed' };
        } finally {
            set((state) => {
                state.saving = { ...state.saving, [stepId]: false };
            });
        }
    },
});
